import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, of, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
    AdminAdminAccess,
    AdminBulkOnboardResult,
    AdminCity,
    AdminDashboardData,
    AdminOnboardUserRequest,
    AdminUpdateUserRequest,
    AdminUser,
    AdminUserCityAccessPayload,
    AdminUserDetailResponse,
    AdminUserStatus
} from './admin-dashboard.models';

// ---- Real API contract ----------------------------------------------------
type TierKey = 'T0' | 'T3' | 'T6';

type AdminApiUserMapping =
    | { ALL: true }
    | ({
          ALL?: false;
          dashboard?: { T0?: string[]; T3?: string[]; T6?: string[] };
          data_upload?: { T3?: string[] };
          checker?: { T3?: string[] };
          calibration?: { T3?: string[] };
      });

interface AdminApiUserRecord {
    sl_no?: number;
    user_email?: string;
    user_status?: string;
    user_admin_access?: string;
    user_mapping?: AdminApiUserMapping;
    user_created_dt?: string | null;
}

interface AdminApiUsersResponse {
    status?: string;
    count?: number;
    users?: AdminApiUserRecord[];
}

interface AdminApiCityRecord {
    city_id?: number;
    city_name?: string;
    country_name?: string;
    city_specification?: string;
    city_t0_status?: string;
    city_t3_status?: string;
    city_t6_status?: string;
    user_count?: number;
    users?: AdminApiUserRecord[];
}

interface AdminApiCitiesResponse {
    status?: string;
    count?: number;
    cities?: AdminApiCityRecord[];
}
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class AdminDashboardService {
    private readonly apiBase = String(environment.apiUrl || '').replace(/\/+$/, '');
    private readonly adminCityDetailsUrl = `${this.apiBase}/admin_city_details`;
    private readonly adminUserDetailsUrl = `${this.apiBase}/admin_user_details`;
    private readonly adminOnboardUserUrl = `${this.apiBase}/admin_onboard_new_user`;
    private readonly adminUserActionUrl = `${this.apiBase}/admin_user_action`;

    private latestCities: AdminCity[] = [];

    constructor(private readonly http: HttpClient) {}

    hasAdminAccess(): boolean {
        return (sessionStorage.getItem('userAdminAccessFromMe') || '')
            .trim()
            .toLowerCase() === 'yes';
    }

    getDashboardData(): Observable<AdminDashboardData> {
        return forkJoin({
            cityResp: this.http
                .get<AdminApiCitiesResponse>(this.adminCityDetailsUrl)
                .pipe(catchError(() => of({ cities: [] } as AdminApiCitiesResponse))),
            userResp: this.http
                .get<AdminApiUsersResponse>(this.adminUserDetailsUrl)
                .pipe(catchError(() => of({ users: [] } as AdminApiUsersResponse)))
        }).pipe(
            map(({ cityResp, userResp }) => {
                const cities = (cityResp.cities ?? []).map((c) => this.mapCity(c));
                this.latestCities = cities;

                const users = (userResp.users ?? [])
                    .map((u) => this.mapUser(u, cities))
                    .sort((a, b) => a.email.localeCompare(b.email));

                return { cities, users };
            }),
            catchError(() => of({ cities: [], users: [] }))
        );
    }

    getUserDetails(email: string): Observable<AdminUserDetailResponse> {
        const params = new HttpParams().set('email', email);
        return this.http.get<AdminApiUsersResponse>(this.adminUserDetailsUrl, { params }).pipe(
            map((resp) => this.mapUserDetails((resp.users ?? [])[0], email)),
            catchError(() => of(this.mapUserDetails(undefined, email)))
        );
    }

    onboardUser(request: AdminOnboardUserRequest): Observable<unknown> {
        return this.http.post(this.adminOnboardUserUrl, {
            user_email: request.email.trim().toLowerCase(),
            user_status: request.status,
            user_admin_access: request.adminAccess ?? 'no',
            user_mapping: this.buildApiUserMapping(request)
        });
    }

    onboardUsers(
        emails: string[],
        shared: Pick<AdminOnboardUserRequest, 'status' | 'adminAccess' | 'allAccess' | 'cityAccess'>
    ): Observable<AdminBulkOnboardResult[]> {
        const requests = emails.map((email) =>
            this.onboardUser({ fullName: '', email, ...shared }).pipe(
                map(() => ({ email, success: true, message: 'onboarded' }) as AdminBulkOnboardResult),
                catchError((err) =>
                    of({
                        email,
                        success: false,
                        message: err?.error?.message || 'failed'
                    } as AdminBulkOnboardResult)
                )
            )
        );
        return requests.length ? forkJoin(requests) : of([]);
    }

    updateUser(userKey: string, request: AdminUpdateUserRequest): Observable<unknown> {
        const userEmail = (request.email || userKey).trim().toLowerCase();
        const payload: Record<string, unknown> = { action: request.action, user_email: userEmail };

        if (request.action === 'update') {
            payload['user_status'] = request.status;
            payload['user_admin_access'] = request.adminAccess ?? 'no';
            payload['user_mapping'] = this.buildApiUserMapping(request);
        } else if (request.action === 'activate' || request.action === 'deactivate') {
            payload['user_status'] = request.action === 'activate' ? 'active' : 'inactive';
        }

        return this.http.post(this.adminUserActionUrl, payload);
    }

    // ---- Mapping helpers --------------------------------------------------

    private mapCity(record: AdminApiCityRecord): AdminCity {
        const t3 = this.isAvailable(record.city_t3_status);
        return {
            id: String(record.city_id ?? record.city_name ?? ''),
            name: (record.city_name ?? '').trim(),
            countryName: record.country_name ?? '',
            citySpecification: record.city_specification ?? 'city',
            onboarded: true,
            t0Active: this.isAvailable(record.city_t0_status),
            t3Active: t3,
            t6Active: this.isAvailable(record.city_t6_status),
            // No dedicated data-upload status in API; data_upload is T3-scoped.
            dataUploadActive: t3
        };
    }

    private mapUser(record: AdminApiUserRecord, cities: AdminCity[]): AdminUser {
        const email = (record.user_email ?? '').trim().toLowerCase();
        const isAll = this.isAllAccess(record.user_mapping);

        return {
            id: email,
            name: this.displayName(email),
            email,
            status: this.normalizeStatus(record.user_status),
            adminAccess: this.normalizeAdmin(record.user_admin_access),
            allAccess: isAll,
            createdOn: record.user_created_dt ?? '',
            permissions: this.mappingToPermissions(record.user_mapping, cities)
        };
    }

    private mapUserDetails(record: AdminApiUserRecord | undefined, fallbackEmail: string): AdminUserDetailResponse {
        const email = (record?.user_email ?? fallbackEmail).trim().toLowerCase();
        const isAll = this.isAllAccess(record?.user_mapping);

        return {
            id: email,
            fullName: this.displayName(email),
            email,
            status: this.normalizeStatus(record?.user_status),
            adminAccess: this.normalizeAdmin(record?.user_admin_access),
            allAccess: isAll,
            cityAccess: this.mappingToPermissions(record?.user_mapping, this.latestCities)
        };
    }

    private mappingToPermissions(
        mapping: AdminApiUserMapping | undefined,
        cities: AdminCity[]
    ): AdminUserCityAccessPayload[] {
        if (!mapping || !cities.length) return [];

        // ALL: true => every city fully enabled.
        if (this.isAllAccess(mapping)) {
            return cities.map((city) => ({
                cityId: city.id,
                cityName: city.name,
                allAccess: true,
                t0Access: true,
                t3Access: true,
                t6Access: true,
                dataUploadAccess: true
            }));
        }

        const m = mapping as Exclude<AdminApiUserMapping, { ALL: true }>;
        return cities
            .map((city) => {
                const t0 = this.includesCity(m.dashboard?.T0, city.name);
                const t3 = this.includesCity(m.dashboard?.T3, city.name);
                const t6 = this.includesCity(m.dashboard?.T6, city.name);
                const upload = this.includesCity(m.data_upload?.T3, city.name);
                return {
                    cityId: city.id,
                    cityName: city.name,
                    allAccess: t0 && t3 && t6 && upload,
                    t0Access: t0,
                    t3Access: t3,
                    t6Access: t6,
                    dataUploadAccess: upload
                };
            })
            .filter((p) => p.t0Access || p.t3Access || p.t6Access || p.dataUploadAccess);
    }

    private buildApiUserMapping(
        request: Pick<AdminOnboardUserRequest, 'allAccess' | 'cityAccess'>
    ): AdminApiUserMapping {
        if (request.allAccess) {
            return { ALL: true };
        }

        const dashboard = { T0: [] as string[], T3: [] as string[], T6: [] as string[] };
        const dataUpload = { T3: [] as string[] };

        for (const city of request.cityAccess) {
            const name = city.cityName || this.latestCities.find((c) => c.id === city.cityId)?.name;
            if (!name) continue;
            if (city.t0Access) dashboard.T0.push(name);
            if (city.t3Access) dashboard.T3.push(name);
            if (city.t6Access) dashboard.T6.push(name);
            if (city.dataUploadAccess) dataUpload.T3.push(name);
        }

        return {
            ALL: false,
            dashboard: {
                T0: this.uniq(dashboard.T0),
                T3: this.uniq(dashboard.T3),
                T6: this.uniq(dashboard.T6)
            },
            data_upload: { T3: this.uniq(dataUpload.T3) },
            checker: { T3: [] },
            calibration: { T3: [] }
        };
    }

    // ---- Primitives -------------------------------------------------------

    private isAllAccess(mapping: AdminApiUserMapping | undefined): boolean {
        return !!mapping && (mapping as { ALL?: boolean }).ALL === true;
    }

    private includesCity(values: string[] | undefined, cityName: string): boolean {
        const target = cityName.trim().toLowerCase();
        return (values ?? []).some((v) => v.trim().toLowerCase() === target);
    }

    private isAvailable(value: string | undefined): boolean {
        return String(value ?? '').trim().toLowerCase() === 'available';
    }

    private normalizeStatus(value: string | undefined): AdminUserStatus {
        return String(value || '').toLowerCase() === 'inactive' ? 'inactive' : 'active';
    }

    private normalizeAdmin(value: string | undefined): AdminAdminAccess {
        return String(value || '').toLowerCase() === 'yes' ? 'yes' : 'no';
    }

    private uniq(values: string[]): string[] {
        return Array.from(new Set(values.filter(Boolean)));
    }

    private displayName(email: string): string {
        return (email.split('@')[0] ?? '')
            .split(/[._-]+/)
            .filter(Boolean)
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join(' ');
    }
}